import QueueItem from './QueueItem.js';
import GroupSpec from './GroupSpec.js';

class SmartQ
{
  constructor() {
    // Array of past items. Index 0: oldest, last index: newest
    this.history = [];
    
    // Array of waiting items. Index 0: now playing, index 1: next, etc.
    this.waitingList = [];
    
    // Number of locked items in the beginning of the queue
    this.numLocked = 2;
    
    // Old/new distribution pattern
    this.pattern = ['new', 'old'];
    
     // Automatic pattern calculation
     this.autoPattern = false;
     this.groupSpec = [];
     this.curGroupNum = 0;
     this.curGroup = null;
     
     this.debugEnabled = false;

     this.lastTime = 0;
  }
  
  // Set number of locked items

  setNumLocked(num) {
    this.numLocked = num;
    this.update();
  }
  
  // Set old/new distribution pattern, e.g. 'new old old', or
  // '1:2' = new/old ratio

  setPattern(patternStr) {
    const ratio = patternStr.split(':');
    if (ratio.length == 2) {
      patternStr = '';
      for (let i = 0; i < ratio[0]; i++)
        patternStr += 'new ';
      for (let i = 0; i < ratio[1]; i++)
        patternStr += 'old ';
    }

    patternStr = patternStr.trim();
    const newPattern = patternStr.split(' ');
    
    for (let word of newPattern) {
      if (word !== 'old' && word !== 'new') {
        throw new Error('Invalid pattern');
      }
    }
    if (newPattern.length == 0) {
      throw new Error('Invalid pattern');
    }
    
    this.pattern = newPattern;
    this.update();
  }

  setAutoPattern(enable) {
    this.autoPattern = enable;
    this.update();
  }

  // Append item/singer to waiting queue and reorder queue.
  // Caller may set itemId, if he wants to identify this exact item
  // after reordering etc.

  addItem(singerId, timeEntered = 0, itemId = 0) {
    if (timeEntered == 0)
      timeEntered = this.getCurrentTime();
    let item = new QueueItem;
    item.itemId = itemId;
    item.singerId = singerId;
    item.timeEntered = timeEntered;
    this.waitingList.push(item);
    this.update();
  }

  // Remove item from waiting list

  removeItem(index) {
    this.waitingList.splice(index, 1);
    this.update();
  }

  // Set item as manually controlled. Any automatic queue changes
  // won't affect this item in the future. It will only move
  // when queue advances.

  setManuallyControlled(index, set = true) {
    this.waitingList[index].isManual = set;
    this.update();
  }

  // Move item. If item is not already manually controlled,
  // it will be from now on.

  moveItem(fromIndex, toIndex) {
    if (toIndex < 0)
      return;

    // Remove from previous pos
    let item = this.waitingList[fromIndex];
    this.waitingList.splice(fromIndex, 1);

    // Set as manual item
    item.isManual = true;

    // Add to new pos
    this.waitingList.splice(toIndex, 0, item);

    this.update();
  }

  //
  // Advance queue state by one (move 'now playing' to history)
  //

  advance() {
    if (this.waitingList.length > 0) {
      // Move from playlist to history
      this.next();
      this.update();
    }
  }

  getHistory() {
    return this.history;
  }

  setHistory(items) {
    for (let item of items) {
      if (item.timePlayed == 0 ||
          item.timeEntered == 0) {
        throw new Error("timePlayed and timeEntered must be set");
      }
    }
    this.history = items;
  }

  getQueue() {
     return this.waitingList;
  }

  setQueue(items) {
    for (let item of items) {
      if (item.timeEntered == 0) {
        throw new Error("timeEntered must be set");
      }
    }
    this.waitingList = items;
  }

  //
  // If debug is enabled, calls to this.debug() will be printed
  // to stdout, otherwise not.
  //

  enableDebug(yes) {
    this.debugEnabled = yes;
  }

  //
  // "Private" functions
  //

  debug(str) {
    if (this.debugEnabled) {
      //console.log(str);
    }
  }

  getCurrentTime() {
    let now = new Date().getTime() / 1000;
    // Make sure time goes forward at each call. Relevant if many items are
    // added rapidly or when simulating queue progress in Order function.
    if (now <= this.lastTime) {
      now = this.lastTime + 0.001;
    }
    this.lastTime = now;
    return now;
  }

  getItemByManualIndex(items, index) {
    for (let item of items) {
      if (item.manualIndex == index)
        return item;
    }
    return null;
  }

  removeItemByManualIndex(items, index) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].manualIndex == index) {
        items.splice(i, 1);
        return;
      }
    }
  }

  getGroupSpecForIndex(index) {
    for (let groupSpec of this.groupSpecs) {
      if (index >= groupSpec.firstIndex &&
          index <= groupSpec.lastIndex) {
        return groupSpec;
      }
    }
    return null;
  }

  next() {
    // Move from playlist to history
    let item = this.waitingList.shift();
    item.timePlayed = this.getCurrentTime();
    this.history.push(item);
  }

  // Find old items and new items and return them in correct order
  analyzeQueue(queue) {
    let oldItems = [];
    let newItems = [];

    // Collect "effective history"
    let effHistory = [];
    for (let i = this.history.length - 1; i >= 0; i--) {
      let item = this.history[i];
      if (item instanceof QueueItem) {
        if (item.isSingerInArray(effHistory) && !item.isManual)
          break;
      }
      effHistory.unshift(item); // prepend
    }

    // Start with time-sorted list
    queue.sort(QueueItem.compareTimeEntered);
 
    // Divide to old and new
    for (let item of queue) {
      const index = item.singerIndexInArray(effHistory);
      if (index != -1) {
        item.indexInEffectiveHistory = index;
        oldItems.push(item);
      }
      else
      {
        newItems.push(item);
      }
    }

    // Sort old items by their position in effective history
    oldItems.sort(QueueItem.compareEffectiveHistoryIndex);

    return [oldItems, newItems];
  }

  //
  // Update waiting queue
  //

  createPatterns() {
    let all = this.history.concat(this.waitingList);
    if (all.length == 0)
      return;
    let group = [];
    let groupNum = all[0].groupNum;

    let i;
    for (i = 0; i < all.length; i++) {
      let item = all[i];
      if (item.groupNum != groupNum) {
        let groupSpec = new GroupSpec;
        groupSpec.pattern = this.createPatternForGroup(group);
        groupSpec.lastIndex = i - 1;
        groupSpec.firstIndex = groupSpec.lastIndex - group.length + 1;
        this.groupSpecs.push(groupSpec);
        console.assert(groupSpec.lastIndex >= groupSpec.firstIndex,
                       "groupSpec.lastIndex >= groupSpec.firstIndex");
        groupNum++;
        group = [];
      }
      group.push(item);
    }

    let groupSpec = new GroupSpec;
    groupSpec.pattern = this.createPatternForGroup(group);
    groupSpec.lastIndex = i - 1;
    groupSpec.firstIndex = groupSpec.lastIndex - group.length + 1;
    this.groupSpecs.push(groupSpec);
  }

  createPatternForGroup(group) {
    let oldItems = [];
    let newItems = [];
    for (let item of group) {
      if (item.selectionReason == 'new')
        newItems.push(item);
      else
        oldItems.push(item);
    }
    let pattern;
    if (oldItems.length > newItems.length)
      pattern = this.distribute(oldItems, newItems);
    else
      pattern = this.distribute(newItems, oldItems);
    return pattern;
  }

  distribute(baselist, list) {
    if (baselist.length == 0) {
      return Array(list.length).fill(list[0].selectionReason);
    } else if (!list) {
      return Array(baselist.length).fill(baselist[0].selectionReason);
    }
    let pattern = [];

    const gap = baselist.length / list.length;
    let nextOffset = gap / 2;
    let nextIndex = Math.round(nextOffset);

    for (let i = 0; i < baselist.length; i++) {
      if (i == nextIndex && list.length) {
        pattern.push(list[0].selectionReason);
        list.shift();
        nextOffset += gap;
        nextIndex = Math.round(nextOffset);
      }
      pattern.push(baselist[i].selectionReason);
    }
    // append rest, if any
    for (let item of list) {
      pattern.push(item.selectionReason);
    }
    
    return pattern;
  }

  update() {
    this.groupSpecs = [];

    this.order();
    if (this.autoPattern) {
      this.createPatterns();
      this.order(false);
    }
  }

  order(setGroupNums = true) {
    let applyWaitlimit = true;

    // Save state
    let history = structuredClone(this.history);

    if (this.history.length == 0)
      this.curGroupNum = 1;
    else
      this.curGroupNum = this.history.slice(-1)[0].groupNum; // from last element

    // Collect manual items from the waiting list
    let manuals = [];
    for (let i = 0, counter = 0; i < this.waitingList.length; i++, counter++) {
       let item = this.waitingList[i];
       if (item.isManual) {
         item.manualIndex = counter;
         manuals.push(item);
         this.waitingList.splice(i, 1); // remove from waiting list
         i--;
       }
    }

    // Simulate queue progress and that way go through the waiting list
    let queue = [];
    for (let i = 0; this.waitingList.length || manuals.length; i++) {
      let item = this.getItemByManualIndex(manuals, i);
      if (item !== null) {
        // Prepend manual item to waiting list
        this.waitingList.unshift(item);
        this.removeItemByManualIndex(manuals, i);
      } else {
        if (this.waitingList.length == 0)
          continue;
        if (i >= this.numLocked)
          this.calcNextQueued(applyWaitlimit);
      }

      let chosenItem = this.waitingList[0];

      if (setGroupNums) {
        // Start of a new group?
        if (this.history.length) {
          let prev = this.history.slice(-1)[0]; // get last element
          if ((prev.waitLimitApplied && !chosenItem.waitLimitApplied)) {
            this.curGroupNum++;
          }
        }
        chosenItem.groupNum = this.curGroupNum;
      }

      queue.push(chosenItem);
      this.next();
    }

    // Set the reordered waiting list
    this.waitingList = queue;

    // Restore state
    this.history = history;
    this.clearTimePlayed(this.waitingList);
  }

  // Calculate which item is next
  calcNextQueued(applyWaitlimit) {
    let [oldItems, newItems] = this.analyzeQueue(this.waitingList);

    let chooseNew = false;
    let waitLimitApplied = false;

    if (oldItems.length && newItems.length) {
      let newItem = newItems[0];
      let oldItem = oldItems[0];

      // Use group specific patterns?
      let groupSpec = this.getGroupSpecForIndex(this.history.length);
      let pattern;
      if (groupSpec != null) {
        pattern = groupSpec.pattern;
      } else {
        pattern = this.pattern;
      }

      // Use modulus operator to get next pattern offset
      let patternOffset = this.history.length % pattern.length;

      // Make sure no old singer performs twice before any new
      let lastIndex = oldItem.lastSingerIndexInArray(this.history);
      if (applyWaitlimit &&
          lastIndex != -1 &&
          !this.history[lastIndex].isManual &&
          newItem.timeEntered < this.history[lastIndex].timePlayed)
      {
        chooseNew = true;
        waitLimitApplied = true;
      }
      // Add new or old based on pattern
      else if (pattern[patternOffset] == 'new') {
        chooseNew = true;
      } else {
        chooseNew = false;
      }
    }
    else
    {
      chooseNew = newItems.length > 0 ? true : false;
    }

    let chosenItem;
    if (chooseNew) {
      chosenItem = newItems.shift();
      chosenItem.selectionReason = 'new';
    } else {
      chosenItem = oldItems.shift();
      chosenItem.selectionReason = 'old';
    }
    chosenItem.waitLimitApplied = waitLimitApplied;

    this.waitingList = [chosenItem].concat(oldItems).concat(newItems);
  }

  clearTimePlayed(items) {
    for (let item of items) {
      item.timePlayed = null;
    }
  }
};

export default SmartQ
